import React, { useState } from 'react';
import api from '../services/api';
import './QuickCreateModal.css';
import { toLocalDateInput } from '@sf/shared';

// ─── Extrai mensagens de erro do Axios ─────────────────────────────
function extractErrors(err) {
  const data = err?.response?.data;
  if (data?.errors) {
    return Object.values(data.errors).flat().join(' · ');
  }
  if (data?.message) return data.message;
  return 'Erro ao salvar. Verifique os campos e tente novamente.';
}

// ─── Catálogo de ícones (emojis) ────────────────────────────────────
const ICON_CATALOG = [
  {
    group: 'Dinheiro',
    icons: ['💰','💵','💴','💶','💷','💸','💳','🏦','📊','📈','📉','💹','🏧','💱','🪙','💎'],
  },
  {
    group: 'Compras',
    icons: ['🛍','🛒','🏪','🏬','🏭','👗','👕','👟','💄','💍','🎁','🎀'],
  },
  {
    group: 'Alimentação',
    icons: ['🍽','🍕','🍔','🍗','🥗','🥩','🥦','☕','🍺','🧃','🍰','🛒'],
  },
  {
    group: 'Casa',
    icons: ['🏠','🏡','🏘','🔑','🚪','💡','🔌','📺','🛋','🪑','🛁','🔒'],
  },
  {
    group: 'Transporte',
    icons: ['🚗','🚕','🚌','🚎','✈','🚢','🚂','🛵','🚲','⛽','🛞','🚦'],
  },
  {
    group: 'Saúde',
    icons: ['🏥','💊','🩺','🧪','💉','🩹','🧘','🦷','👁','🫀','🩻','🧬'],
  },
  {
    group: 'Educação',
    icons: ['🎓','📚','📖','🎒','🏫','✏','📐','📏','🖊','📓','🔬','🧮'],
  },
  {
    group: 'Trabalho',
    icons: ['💼','🏢','📋','📝','📌','📎','💻','📱','🖥','📞','📠','🖨'],
  },
  {
    group: 'Serviços',
    icons: ['🔧','🔨','⚙','🪛','🔩','🪚','🔌','📡','🔦','🪤','🧰','🛠'],
  },
  {
    group: 'Lazer',
    icons: ['🎭','🎬','🎮','🎵','🎸','🎨','📷','⚽','🏃','🏊','🎯','🎲'],
  },
  {
    group: 'Outros',
    icons: ['⭐','🌟','💫','✨','🎉','🏆','🌱','♻','🌍','❤','🔖','🏷'],
  },
];

function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (icon) => {
    onChange(icon);
    setOpen(false);
  };

  return (
    <div className="icon-picker-wrap">
      <div className="icon-picker-trigger-row">
        <div className="icon-picker-preview">{value || '—'}</div>
        <button
          type="button"
          className="icon-picker-toggle"
          onClick={() => setOpen(o => !o)}
        >
          {open ? 'Fechar catálogo' : 'Abrir catálogo'}
        </button>
        {value && (
          <button type="button" className="icon-picker-clear" onClick={() => onChange('')} title="Remover ícone">✕</button>
        )}
      </div>

      {open && (
        <div className="icon-catalog">
          {ICON_CATALOG.map(group => (
            <div key={group.group} className="icon-group">
              <span className="icon-group-label">{group.group}</span>
              <div className="icon-grid">
                {group.icons.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    className={`icon-btn ${value === icon ? 'selected' : ''}`}
                    onClick={() => handleSelect(icon)}
                    title={icon}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Categoria ──────────────────────────────────────────────────────
function QuickCategoryForm({ context, onCreated, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    type: context,
    color: context === 'Receita' ? '#22c55e' : '#ef4444',
    icon: '',
    status: 'Ativa',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    api.post('/categories', formData)
      .then(res => onCreated(res.data))
      .catch(err => { setSaving(false); setError(extractErrors(err)); });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="qc-field">
        <label className="qc-label">Tipo</label>
        <div
          className="qc-type-badge"
          style={{
            background: context === 'Receita' ? '#dcfce7' : '#fee2e2',
            color:      context === 'Receita' ? '#16a34a' : '#dc2626',
          }}
        >
          {context === 'Receita' ? '📈' : '📉'} {context}
        </div>
      </div>

      <div className="qc-field">
        <label className="qc-label" htmlFor="qc-cat-name">Nome *</label>
        <input
          id="qc-cat-name"
          className="qc-input"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="Nome da categoria"
          required
          autoFocus
        />
      </div>

      <div className="qc-row">
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Ícone</label>
          <IconPicker
            value={formData.icon}
            onChange={icon => setFormData({ ...formData, icon })}
          />
        </div>
        <div className="qc-field" style={{ minWidth: '80px' }}>
          <label className="qc-label" htmlFor="qc-cat-color">Cor</label>
          <input
            id="qc-cat-color"
            className="qc-color"
            type="color"
            value={formData.color}
            onChange={e => setFormData({ ...formData, color: e.target.value })}
          />
        </div>
      </div>

      {error && <p className="qc-error">{error}</p>}

      <div className="qc-footer">
        <button type="button" className="qc-btn-cancel" onClick={onClose}>Cancelar</button>
        <button type="submit" className="qc-btn-save" disabled={saving}>
          {saving ? 'Salvando...' : 'Criar Categoria'}
        </button>
      </div>
    </form>
  );
}

// ─── Conta Bancária ─────────────────────────────────────────────────
function QuickBankForm({ onCreated, onClose }) {
  const today = toLocalDateInput(new Date());
  const [formData, setFormData] = useState({
    name: '',
    account_type: 'Corrente',
    code: '',
    agency: '',
    account_number: '',
    initial_balance: '0',
    opening_date: today,
    color: '#6366f1',
    status: 'Ativa',
    overdraft_limit: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const accountTypes = [
    { id: 'Corrente',     label: 'Corrente',  icon: '🏦' },
    { id: 'Poupança',     label: 'Poupança',  icon: '🐖' },
    { id: 'Investimento', label: 'Invest.',   icon: '📈' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    api.post('/banks', formData)
      .then(res => onCreated(res.data))
      .catch(err => { setSaving(false); setError(extractErrors(err)); });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="qc-field">
        <label className="qc-label">Tipo de Conta</label>
        <div className="qc-toggle-group">
          {accountTypes.map(t => (
            <button
              key={t.id}
              type="button"
              className={`qc-toggle-btn ${formData.account_type === t.id ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, account_type: t.id })}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="qc-field">
        <label className="qc-label" htmlFor="qc-bank-name">Nome *</label>
        <input
          id="qc-bank-name"
          className="qc-input"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Banco do Brasil"
          required
          autoFocus
        />
      </div>

      <div className="qc-row">
        <div className="qc-field" style={{ width: '90px', flexShrink: 0 }}>
          <label className="qc-label">Código *</label>
          <input
            className="qc-input"
            type="text"
            value={formData.code}
            onChange={e => setFormData({ ...formData, code: e.target.value })}
            placeholder="001"
            required
          />
        </div>
        <div className="qc-field" style={{ width: '110px', flexShrink: 0 }}>
          <label className="qc-label">Agência *</label>
          <input
            className="qc-input"
            type="text"
            value={formData.agency}
            onChange={e => setFormData({ ...formData, agency: e.target.value })}
            placeholder="0001"
            required
          />
        </div>
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Nº da Conta *</label>
          <input
            className="qc-input"
            type="text"
            value={formData.account_number}
            onChange={e => setFormData({ ...formData, account_number: e.target.value })}
            placeholder="00000-0"
            required
          />
        </div>
      </div>

      <div className="qc-row">
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Saldo Inicial (R$) *</label>
          <input
            className="qc-input"
            type="number"
            step="0.01"
            value={formData.initial_balance}
            onChange={e => setFormData({ ...formData, initial_balance: e.target.value })}
            required
          />
        </div>
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Data de Abertura *</label>
          <input
            className="qc-input"
            type="date"
            value={formData.opening_date}
            onChange={e => setFormData({ ...formData, opening_date: e.target.value })}
            required
          />
        </div>
      </div>

      {error && <p className="qc-error">{error}</p>}

      <div className="qc-footer">
        <button type="button" className="qc-btn-cancel" onClick={onClose}>Cancelar</button>
        <button type="submit" className="qc-btn-save" disabled={saving}>
          {saving ? 'Salvando...' : 'Criar Conta'}
        </button>
      </div>
    </form>
  );
}

// ─── Cliente ────────────────────────────────────────────────────────
function QuickCustomerForm({ onCreated, onClose }) {
  const [formData, setFormData] = useState({ name: '', document: '', email: '', status: 'Ativo' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    api.post('/customers', formData)
      .then(res => onCreated(res.data))
      .catch(err => { setSaving(false); setError(extractErrors(err)); });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="qc-field">
        <label className="qc-label" htmlFor="qc-cust-name">Nome / Razão Social *</label>
        <input
          id="qc-cust-name"
          className="qc-input"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="Nome do cliente"
          required
          autoFocus
        />
      </div>

      <div className="qc-row">
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Documento (Opcional)</label>
          <input
            className="qc-input"
            type="text"
            value={formData.document}
            onChange={e => setFormData({ ...formData, document: e.target.value })}
            placeholder="CPF ou CNPJ"
          />
        </div>
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Email (Opcional)</label>
          <input
            className="qc-input"
            type="email"
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@..."
          />
        </div>
      </div>

      {error && <p className="qc-error">{error}</p>}

      <div className="qc-footer">
        <button type="button" className="qc-btn-cancel" onClick={onClose}>Cancelar</button>
        <button type="submit" className="qc-btn-save" disabled={saving}>
          {saving ? 'Salvando...' : 'Criar Cliente'}
        </button>
      </div>
    </form>
  );
}

// ─── Fornecedor ─────────────────────────────────────────────────────
function QuickProviderForm({ onCreated, onClose }) {
  const [formData, setFormData] = useState({ name: '', document: '', email: '', type: 'Fornecedor', status: 'Ativo' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const providerTypes = ['Fornecedor', 'Prestador de Serviço', 'Ambos'];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    api.post('/providers', formData)
      .then(res => onCreated(res.data))
      .catch(err => { setSaving(false); setError(extractErrors(err)); });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="qc-field">
        <label className="qc-label">Tipo</label>
        <div className="qc-toggle-group">
          {providerTypes.map(t => (
            <button
              key={t}
              type="button"
              className={`qc-toggle-btn ${formData.type === t ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, type: t })}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="qc-field">
        <label className="qc-label" htmlFor="qc-prov-name">Nome / Razão Social *</label>
        <input
          id="qc-prov-name"
          className="qc-input"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="Nome do fornecedor"
          required
          autoFocus
        />
      </div>

      <div className="qc-row">
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Documento (Opcional)</label>
          <input
            className="qc-input"
            type="text"
            value={formData.document}
            onChange={e => setFormData({ ...formData, document: e.target.value })}
            placeholder="CPF ou CNPJ"
          />
        </div>
        <div className="qc-field qc-field-grow">
          <label className="qc-label">Email (Opcional)</label>
          <input
            className="qc-input"
            type="email"
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@..."
          />
        </div>
      </div>

      {error && <p className="qc-error">{error}</p>}

      <div className="qc-footer">
        <button type="button" className="qc-btn-cancel" onClick={onClose}>Cancelar</button>
        <button type="submit" className="qc-btn-save" disabled={saving}>
          {saving ? 'Salvando...' : 'Criar Fornecedor'}
        </button>
      </div>
    </form>
  );
}

// ─── Modal Wrapper ──────────────────────────────────────────────────
const MODAL_TITLES = {
  category: 'Nova Categoria',
  bank:     'Nova Conta Bancária',
  customer: 'Novo Cliente',
  provider: 'Novo Fornecedor',
};

function QuickCreateModal({ type, context, onCreated, onClose }) {
  const renderForm = () => {
    switch (type) {
      case 'category': return <QuickCategoryForm context={context} onCreated={onCreated} onClose={onClose} />;
      case 'bank':     return <QuickBankForm onCreated={onCreated} onClose={onClose} />;
      case 'customer': return <QuickCustomerForm onCreated={onCreated} onClose={onClose} />;
      case 'provider': return <QuickProviderForm onCreated={onCreated} onClose={onClose} />;
      default:         return null;
    }
  };

  return (
    <div
      className="qc-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="qc-box">
        <div className="qc-header">
          <span className="qc-title">{MODAL_TITLES[type]}</span>
          <button type="button" className="qc-close" onClick={onClose} aria-label="Fechar">✕</button>
        </div>
        <div className="qc-body">
          {renderForm()}
        </div>
      </div>
    </div>
  );
}

export default QuickCreateModal;
